// src/components/layout/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import ThemeToggle from '../ui/ThemeToggle';
import styles from '../../styles/Navbar.module.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isDarkMode } = useTheme();

  // Check if we're on mobile on component mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Setup event listener
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  useEffect(() => {
    // Prevent scrolling when mobile menu is open
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, isMobile]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Reduced to 3 navigation items instead of 5
  const navItems = [
    { name: "Overview", href: "#overview" },
    { name: "Resources", href: "#resources" },
    { name: "Our Partners", href: "#infrastructure" },
  ];

  const navbarClasses = [
    styles.navbar,
    scrolled ? (isDarkMode ? styles.navbarDark : styles.navbarLight) : styles.transparent
  ].join(' ');

  return (
    <header className={navbarClasses}>
      <div className={styles.container}>
        {/* Logo */}
        <a href="/" className={styles.logo}>
          <span className={styles.logoBrand}>Daftra</span>
          <span className={isDarkMode ? styles.logoTextDark : styles.logoTextLight}>API</span>
        </a>

        <div className={styles.rightControls}>
          {/* Desktop Navigation - Horizontal Menu */}
          <nav className={styles.desktopMenu}>
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`${styles.navItem} ${isDarkMode ? styles.navItemDark : styles.navItemLight}`}
              >
                {item.name}
              </a>
            ))}
            
            <a
              href="https://docs.daftara.dev/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.apiDocButton}
            >
              API Documentation
            </a>
          </nav>
          
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className={`${styles.mobileMenuButton} ${isDarkMode ? styles.mobileMenuButtonDark : styles.mobileMenuButtonLight}`}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X size={24} />
            ) : (
              <Menu size={24} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile full-width menu */}
      <AnimatePresence>
        {isOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'calc(100vh - 72px)' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`${styles.mobileMenu} ${isDarkMode ? styles.mobileMenuDark : styles.mobileMenuLight}`}
          >
            <div className={styles.mobileMenuContent}>
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className={`${styles.mobileNavItem} ${isDarkMode ? styles.mobileNavItemDark : styles.mobileNavItemLight}`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              <a
                href="https://docs.daftara.dev/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.mobileApiDocButton}
                onClick={() => setIsOpen(false)}
              >
                API Documentation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay to close menu when clicking outside - only needed for desktop dropdowns */}
      {isOpen && !isMobile && (
        <div className={styles.overlay} onClick={() => setIsOpen(false)} />
      )}
    </header>
  );
};

export default Navbar;